import { Injectable, signal } from '@angular/core';
import { OnboardingStepNumber, StepGuideId } from '../models';
import { OnboardingStore } from '../state';
import {
  ConfigurationService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import {
  ConfigurationSettingCodes,
  ConfigurationSetting,
} from '@maximizer/core/shared/domain';
import { catchError, of, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { TrackerService } from './tracker.service';
import {
  EventAction,
  EventJourney,
  EventModule,
} from '@maximizer/outlook/shared/domain';

@Injectable()
export class OnboardingService {
  private readonly onboardingSettingCodes: ConfigurationSettingCodes = {
    code3: 'OutlookOnboarding',
    code4: 'Settings',
  };

  readonly StepGuideId = StepGuideId;
  readonly OnboardingStepNumber = OnboardingStepNumber;

  onboardingSettings: ConfigurationSetting | null = null;
  showOnboardingDialog = signal<boolean>(false);
  showExitDialog = signal<boolean>(false);
  showRemindLater = signal<boolean>(false);
  isLastStep = signal<boolean>(false);

  constructor(
    private readonly onboardingStore: OnboardingStore,
    private readonly globalStore: GlobalStore,
    private readonly configurationService: ConfigurationService,
    private readonly router: Router,
    private readonly trackerService: TrackerService,
  ) {}

  start(): void {
    if (
      !this.router.url.startsWith('/home') &&
      !this.router.url.startsWith('/read-email')
    ) {
      this.router.navigate(['/home']);
    }
    this.onboardingStore.setOnboardingInProgress(true);
    this.showOnboardingDialog.set(false);
    this.onboardingStore.setCurrentStep(OnboardingStepNumber.Step1);
    this.onboardingStore.setCurrentGuide(StepGuideId.AddContact1);

    this.trackerService.trackEvent({
      module: EventModule.Onboarding,
      journey: EventJourney.Started,
      action: EventAction.Clicked,
    });
  }

  completeStep(step: OnboardingStepNumber): void {
    this.onboardingStore.setCurrentStep(step);
    this.trackerService.trackEvent({
      module: EventModule.Onboarding,
      journey: EventJourney.Step,
      action: EventAction.Completed,
      metadata: { step },
    });
  }

  reset(): void {
    this.onboardingStore.setCurrentStep(OnboardingStepNumber.Step1);
    this.isLastStep.set(false);
  }

  quit(): void {
    this.trackerService.trackEvent({
      module: EventModule.Onboarding,
      journey: EventJourney.Exit,
      action: EventAction.Clicked,
      metadata: {
        step: this.onboardingStore.currentStep(),
        guide: this.onboardingStore.currentGuide(),
      },
    });
    this.reset();
    this.onboardingStore.setOnboardingInProgress(false);
    this.showOnboardingDialog.set(false);
    this.showExitDialog.set(false);
  }

  finish(): void {
    this.onboardingStore.setOnboardingInProgress(false);
    this.showOnboardingDialog.set(false);
    this.isLastStep.set(false);
    this.saveSettings();

    this.trackerService.trackEvent({
      module: EventModule.Onboarding,
      journey: EventJourney.Finished,
      action: EventAction.Completed,
    });
  }

  remindLater(): void {
    this.showRemindLater.set(true);
    this.showOnboardingDialog.set(false);
    this.trackerService.trackEvent({
      module: EventModule.Onboarding,
      journey: EventJourney.RemindLater,
      action: EventAction.Clicked,
    });
  }

  getSettings(): void {
    const session = this.globalStore.session();

    if (session) {
      this.onboardingSettingCodes.code2 = session.user.id;

      this.configurationService
        .getSetting(this.onboardingSettingCodes)
        .pipe(
          switchMap((setting) => {
            if (setting) {
              this.onboardingSettings = setting;
              this.onboardingStore.setConfigurationId(
                this.onboardingSettings.id,
              );

              if (this.onboardingSettings?.textValue) {
                const parsedValue = JSON.parse(
                  this.onboardingSettings.textValue,
                );
                this.onboardingStore.hasCompleted(parsedValue.completed);
              }

              return of(setting);
            } else {
              const defaultSetting = JSON.stringify({ completed: false });
              this.showOnboardingDialog.set(true);

              return this.configurationService
                .updateSetting(
                  null,
                  this.onboardingSettingCodes,
                  defaultSetting,
                )
                .pipe(
                  tap((newSetting) => {
                    if (newSetting) {
                      this.onboardingSettings = newSetting;
                      this.onboardingStore.setConfigurationId(
                        this.onboardingSettings.id,
                      );
                    }
                  }),
                );
            }
          }),
        )
        .subscribe();
    }
  }

  saveSettings(): void {
    const completedSetting = JSON.stringify({ completed: true });
    const onboardingStateLocalStorage =
      sessionStorage.getItem('onboardingState');

    if (onboardingStateLocalStorage) {
      const onboardingStore = JSON.parse(onboardingStateLocalStorage);
      // Not `??` (sonar S6606): initialState persists configurationId as '', which is not nullish but must still mean "create".
      const configurationId = onboardingStore.configurationId || null;

      this.configurationService
        .updateSetting(
          configurationId,
          this.onboardingSettingCodes,
          completedSetting,
        )
        .pipe(
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe(() => {
          this.onboardingStore.hasCompleted(true);
        });
    }
  }
}
